import { FocusTrap } from "@mantine/core";
import { t } from "i18next";
import { useCallback, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { colors } from "#app/theme/v1/colors";
import { useCompanyMachines } from "#modules/machines/view/useCompanyMachines";
import { CreateProcessStep } from "#modules/processSteps/infra/createProcessStep.api";
import { UpdateProcessStepResponseDto } from "#modules/processSteps/infra/updateProcessStep.api";
import { useUpdateProcessStep } from "#modules/processSteps/view/useUpdateProcessStep";
import { Alert } from "#modules/quoteRequestLines/domain/alert";
import { isAddedValueProcessStep } from "#modules/quoteRequestLines/getDefaultValues";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import { AddSectionHeader } from "#modules/quoteRequestLines/views/DetailsPanel/AddSectionHeader";
import { ProcessStepSelect } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/ProcessStepSelect";
import {
  ProcessStepForTable,
  ProcessStepsTable,
} from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/ProcessStepsTable";
import { useProcessStepsSelect } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/useProcessStepsSelect";
import { SuggestedAssemblyProcessSteps } from "#modules/quoteRequestLines/views/DetailsPanel/SuggestedAssemblyProcessSteps";
import { useAddPricedProcessStep } from "#modules/quoteRequestLines/views/useAddPricedProcessStep";
import { useAnalytics, AnalyticsEvent } from "#shared/useAnalytics";
import { Flex } from "#shared/view/components/Flex";
import { Stack } from "#shared/view/components/Stack";
import { CloseButton } from "#shared/view/designSystem/CloseButton";
import { IndustrialProcessType, PartTypeSchema } from "shared";

import { PartialProcessStep } from "./ProcessStep";

export const filterInHouseProcessSteps = <T extends { industrialProcess: IndustrialProcessType }>(
  processSteps: T[],
) => processSteps.filter((ps) => isAddedValueProcessStep(ps));

type ProcessStepsProps = {
  onProcessStepCreated?: (processStep: CreateProcessStep) => void;
  onProcessStepUpdated?: (updatedProcessStep: UpdateProcessStepResponseDto) => void;
  onProcessStepDeleted?: () => void;
  alerts?: Alert[];
  hasChildren: boolean;
  processSteps: ProcessStepForTable[];
};

export const ProcessSteps = ({
  onProcessStepCreated,
  onProcessStepUpdated,
  onProcessStepDeleted,
  alerts = [],
  hasChildren,
  processSteps,
}: ProcessStepsProps) => {
  const [processStepSelectIsDisplayed, setProcessStepSelectDisplay] = useState(false);

  const { setValue, control } = useFormContext<QuoteRequestLineFormData>();
  const analytics = useAnalytics();

  const { updateProcessStep } = useUpdateProcessStep({
    onSuccess: onProcessStepUpdated,
  });

  const quoteRequestLineId = useWatch({ control, name: "quoteRequestLineId" });
  const part = useWatch({ control, name: "part" });
  const materialGrade = useWatch({ control, name: "materialGrade" });

  const { addPricedProcessStep, isPending: isAddingProcessStep } = useAddPricedProcessStep({
    onSuccess: (processStep) => {
      onProcessStepCreated?.(processStep);
    },
  });

  const { companyMachines } = useCompanyMachines();
  const shouldShowCompanyMachines = companyMachines.length > 0;

  const computeNewIndex = useCallback(() => {
    const indexes = processSteps.map((ps) => ps.index);

    if (indexes.length === 0) {
      return 0;
    }

    return Math.max(...indexes) + 1;
  }, [processSteps]);

  const addProcessStep = async (value: string | null) => {
    if (!quoteRequestLineId || !materialGrade) {
      return;
    }

    const processSteptoAdd = getProcessStepToAddFromSelect(value);

    if (!processSteptoAdd) {
      return;
    }

    const newIndex = computeNewIndex();

    analytics.capture(AnalyticsEvent.DetailsPanelProcessStepAdded, {
      quote_request_line_id: quoteRequestLineId,
      industrial_process: processSteptoAdd.industrialProcess,
      process_key: "processKey" in processSteptoAdd ? processSteptoAdd.processKey : null,
    });

    await addPricedProcessStep({
      quoteRequestLineId,
      industrialProcess: processSteptoAdd.industrialProcess,
      description: processSteptoAdd.description ?? null,
      processKey: "processKey" in processSteptoAdd ? processSteptoAdd.processKey : null,
      index: newIndex,
      materialGrade,
    });

    hideProcessStepSelect();
  };

  const showProcessStepSelect = () => {
    setProcessStepSelectDisplay(true);
  };

  const hideProcessStepSelect = () => {
    setProcessStepSelectDisplay(false);
  };

  const handleReorder = useCallback(
    (reorderedProcessSteps: PartialProcessStep[]) => {
      const updatedProcessSteps = reorderedProcessSteps.map((ps, idx) => ({
        ...ps,
        index: idx,
      }));
      setValue("processSteps", updatedProcessSteps);

      analytics.capture(AnalyticsEvent.DetailsPanelProcessStepsReordered, {
        quote_request_line_id: quoteRequestLineId,
        process_step_count: reorderedProcessSteps.length,
      });

      reorderedProcessSteps.forEach((ps, newIndex) => {
        if (ps.id && ps.index !== newIndex) {
          updateProcessStep({
            id: ps.id,
            payload: { index: newIndex },
          });
        }
      });
    },
    [setValue, updateProcessStep, analytics, quoteRequestLineId],
  );

  const { allProcessStepsDataForSelect, getProcessStepToAddFromSelect, allPredictedProcessSteps } =
    useProcessStepsSelect(filterInHouseProcessSteps);

  return (
    <>
      <AddSectionHeader
        label={t("quoteRequestLine.processSteps")}
        color={colors.violet[5]}
        onAdd={showProcessStepSelect}
        isLoading={isAddingProcessStep}
      />

      {processStepSelectIsDisplayed && (
        <FocusTrap>
          <Flex align={"center"} justify={"space-between"} pb={"xs"}>
            <ProcessStepSelect
              data-testid="process-step-select"
              placeholder={t("quoteRequestLine.addProcessStepPlaceholder")}
              data={allProcessStepsDataForSelect}
              data-autofocus
              onChange={addProcessStep}
              value={""}
              disabled={isAddingProcessStep}
              collapsedGroupLabels={[t("processStep.otherFaberaProcessSteps")]}
            />

            <CloseButton size="sm" onClick={hideProcessStepSelect} />
          </Flex>
        </FocusTrap>
      )}

      <ProcessStepsTable
        showCompanyMachines={shouldShowCompanyMachines}
        processSteps={processSteps}
        alerts={alerts}
        onProcessStepUpdated={onProcessStepUpdated}
        onProcessStepDeleted={onProcessStepDeleted}
        onReorder={handleReorder}
      />

      <Stack>
        {part?.type !== PartTypeSchema.enum.MACHINING && (
          <SuggestedAssemblyProcessSteps
            allPredictedProcessSteps={allPredictedProcessSteps}
            addProcessStep={addProcessStep}
            hasChildren={hasChildren}
            partId={part?.id}
          />
        )}
      </Stack>
    </>
  );
};
